import React from 'react';
import { Typography, Paper, Box, Divider, List, ListItem, ListItemText } from '@mui/material';

const RifSurvivalGuide = () => {
  return (
    <Paper sx={{ p: 3, mt: 3, mb: 3, maxWidth: '100%', bgcolor: 'rgba(0,0,0,0.7)' }}>
      <Box 
        sx={{ 
          height: '200px', 
          width: '100%', 
          bgcolor: '#4caf50', 
          borderRadius: 1, 
          mb: 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Typography variant="h4" color="white">
          RIF Survival Guide
        </Typography>
      </Box>
      
      <Typography variant="h4" gutterBottom align="left">
        Surviving a Federal RIF: Your Practical Guide
      </Typography>
      
      <Typography variant="subtitle1" gutterBottom align="left">
        Strategies, resources, and practical steps for federal employees facing a Reduction in Force.
      </Typography>
      
      <Divider sx={{ my: 2 }} />
      
      <Typography variant="h5" gutterBottom sx={{ mt: 3 }} align="left">
        Before the RIF: Preparation Strategies
      </Typography>
      
      <Typography variant="body1" paragraph align="left">
        When your agency announces a potential RIF, taking immediate steps can improve your position:
      </Typography>
      
      <List sx={{ pl: 2 }}>
        <ListItem>
          <ListItemText 
            primary="Review Your Personnel Records" 
            secondary="Verify your service computation date, performance ratings, and veterans' preference status. These factors affect your retention standing."
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Understand Your Competitive Area" 
            secondary="Know which organizational unit and geographic location constitute your competitive area, as RIF procedures apply within these boundaries."
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Learn Your Retention Standing" 
            secondary="Your tenure group, veterans' preference, and performance ratings determine your standing. Higher standing means better protection from RIF actions."
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Update Your Resume" 
            secondary="Refresh your federal resume and private sector version to be prepared for both internal reassignments and external opportunities."
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Document Your Achievements" 
            secondary="Compile documentation of your accomplishments, specialized skills, and training that might support your placement in remaining positions."
          />
        </ListItem>
      </List>
      
      <Typography variant="h5" gutterBottom sx={{ mt: 4 }} align="left">
        During the RIF: Know Your Rights
      </Typography>
      
      <Typography variant="body1" paragraph align="left">
        Understanding your entitlements during a RIF can help you navigate the process more effectively:
      </Typography>
      
      <Box sx={{ pl: 2 }}>
        <Typography variant="h6" gutterBottom align="left">
          Bump and Retreat Rights
        </Typography>
        <Typography variant="body1" paragraph align="left">
          You may have the right to "bump" an employee in a lower retention subgroup or "retreat" to a position you previously held. These rights can help you remain employed, though potentially at a lower grade.
        </Typography>
        
        <Typography variant="h6" gutterBottom align="left">
          Assignment Rights
        </Typography>
        <Typography variant="body1" paragraph align="left">
          Competitive service employees with career or career-conditional appointments who have received a RIF separation notice have "assignment rights" to positions they qualify for at the same grade level.
        </Typography>
        
        <Typography variant="h6" gutterBottom align="left">
          Appeal Rights
        </Typography>
        <Typography variant="body1" paragraph align="left">
          If you believe your agency incorrectly applied RIF procedures, you can appeal to the Merit Systems Protection Board (MSPB) within 30 days of the effective date of the RIF action.
        </Typography>
        
        <Typography variant="h6" gutterBottom align="left">
          VERA/VSIP Opportunities
        </Typography>
        <Typography variant="body1" paragraph align="left">
          Some agencies offer Voluntary Early Retirement Authority (VERA) or Voluntary Separation Incentive Payments (VSIP) before implementing a RIF. These programs can provide financial incentives for voluntary separation.
        </Typography>
      </Box>
      
      <Typography variant="h5" gutterBottom sx={{ mt: 4 }} align="left">
        Finding New Federal Employment
      </Typography>
      
      <Typography variant="body1" paragraph align="left">
        If you receive a RIF notice, you gain special hiring advantages:
      </Typography>
      
      <List sx={{ pl: 2 }}>
        <ListItem>
          <ListItemText 
            primary="Career Transition Assistance Plan (CTAP)" 
            secondary="Provides selection priority for vacancies within your agency in the local commuting area."
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Interagency Career Transition Assistance Plan (ICTAP)" 
            secondary="Offers selection priority for positions in other agencies if you're qualified and your performance was at least fully successful."
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Reemployment Priority List (RPL)" 
            secondary="Your agency must give you priority consideration for future vacancies for which you're qualified."
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="USAJobs and Hiring Fairs" 
            secondary="Create saved searches on USAJobs.gov and attend federal hiring fairs, which sometimes have on-the-spot hiring authority."
          />
        </ListItem>
      </List>
      
      <Typography variant="h5" gutterBottom sx={{ mt: 4 }} align="left">
        Financial Planning for RIF Separation
      </Typography>
      
      <Typography variant="body1" paragraph align="left">
        If you're ultimately separated through a RIF, take these steps to manage your finances:
      </Typography>
      
      <List sx={{ pl: 2 }}>
        <ListItem>
          <ListItemText 
            primary="Calculate Your Severance Package" 
            secondary="Understand how much severance pay you'll receive and for how long. Use the official OPM calculator or agency tools to get accurate estimates."
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Apply for Unemployment Benefits" 
            secondary="Federal employees separated by RIF generally qualify for unemployment compensation. Apply through your state's unemployment office."
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Review Health Insurance Options" 
            secondary="You may continue FEHB coverage under Temporary Continuation of Coverage (TCC) for up to 18 months, though you'll pay the full premium plus 2%."
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Manage Your TSP Account" 
            secondary="Decide whether to leave your funds in the Thrift Savings Plan, transfer to another qualified retirement account, or withdraw (with potential tax consequences)."
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Consider Deferred Retirement" 
            secondary="If you're close to retirement eligibility, check if you qualify for deferred retirement instead of withdrawing your retirement contributions."
          />
        </ListItem>
      </List>
      
      <Typography variant="h5" gutterBottom sx={{ mt: 4 }} align="left">
        Emotional Well-being During a RIF
      </Typography>
      
      <Typography variant="body1" paragraph align="left">
        The RIF process can be emotionally challenging. Consider these approaches:
      </Typography>
      
      <List sx={{ pl: 2 }}>
        <ListItem>
          <ListItemText 
            primary="Use Employee Assistance Program (EAP)" 
            secondary="Take advantage of free, confidential counseling through your agency's EAP to manage stress and anxiety."
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Network with Colleagues" 
            secondary="Maintain professional relationships; they can provide emotional support and potential job leads."
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Maintain a Routine" 
            secondary="Keeping a structured schedule helps combat the uncertainty of the RIF process."
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Focus on Growth" 
            secondary="Use this transition period to develop new skills or pursue training that enhances your employability."
          />
        </ListItem>
      </List>
      
      <Divider sx={{ my: 3 }} />
      
      <Typography variant="body2" sx={{ fontStyle: 'italic' }} align="left">
        Remember: A RIF is an organizational action, not a reflection of your value as an employee. Many federal workers have successfully navigated RIFs and continued their careers in government service or found rewarding opportunities elsewhere. With preparation and knowledge of your rights, you can better navigate this challenging transition.
      </Typography>
    </Paper>
  );
};

export default RifSurvivalGuide; 